import {
  ChoiceCallout,
  ProductOption,
  ProductOptionChangedEvent,
  ProductOptionListModel,
  ProductOptionsTranslations,
} from "@vp/product-options-ui";
import React, { useContext, useEffect, useState } from "react";
import PageConfig from "../../utils/PageConfig";
import { PageStaticDataContext } from "../../contexts/PageStaticDataContextProvider";
import { BffDataContext } from "../../contexts/BffDataContextProvider";
import { PluginDataContext } from "../../pluginsInfrastructure/PluginDataContextProvider";
import { ProductContext } from "../../contexts/ProductContextProvider";
import TrackingUtil from "../../utils/tracking";
import { PricingContext } from "../../contexts/PricingContext";
import { useScreenClass } from "@vp/swan";
import { getAction } from "../../services/siteflow";
import { ensureUserTestId } from "@vp/ab-test-cookie";
import { getScreenClass } from "../../utils/screenClass";
import { bulkOrderBigQuantity, showBulkOrder } from "../../utils/bulkOrder";
import TokenEngine from "@vp/vp-js-token-engine";
import { SAMPLE_QUANTITY_PRDS } from "./sampleQuantityProducts";
import AlbelliQtyCookieProvider from "../../utils/albelliQtyCookieProvider";

export const ProductOptionQuantitySelectors = ({
  savingsFormat,
}: {
  savingsFormat: "percent" | "difference" | "hidden";
}) => {
  const [bulkOrderUrl, setBulkOrderUrl] = useState<string | undefined>();
  const [bulkQuantitySelected, setBulkQuantitySelected] = useState(false);
  const { locale, mpv } = useContext(PageStaticDataContext);
  const { prices, compatibleQuantities } = useContext(BffDataContext);
  const { pluginData } = useContext(PluginDataContext);
  const { clientSideStateInitialized, selectedQuantity, selectQuantity } = useContext(
    ProductContext
  );
  const { vatInclusivity } = useContext(PricingContext);
  const { isMobile } = getScreenClass(useScreenClass());

  useEffect(() => {
    let mounted = true;
    const getBulkOrderUrl = async () => {
      const siteflowResponse = await getAction({
        tenant: PageConfig.tenant,
        culture: locale,
        flowAction: "bulkorder",
        productKey: mpv.coreProductId,
        version: mpv.version,
        optionValues: {},
        quantity: undefined,
        optimizelyEndUserId: ensureUserTestId(),
        isMobile,
        mpvId: mpv.mpvId,
      });
      if (mounted) {
        setBulkOrderUrl(siteflowResponse?.url);
      }
    };
    getBulkOrderUrl();
    return () => {
      mounted = false;
    };
  }, [isMobile]);

  // UseEffect is a Holiday hack, for Albelli Products, save the QTY into a cookie
  useEffect(() => {
    if (!bulkQuantitySelected && selectedQuantity) {
      if (["wallCalendars", "deskCalendars", "photobooks"].includes(mpv.mpvId)) {
        AlbelliQtyCookieProvider.setAlbelliQty(selectedQuantity);
      }
    }
  }, [bulkQuantitySelected, selectedQuantity, mpv.mpvId]);

  const quantityLabel = pluginData.contentful["quantity"] || "";
  const recommendedLabel = pluginData.contentful["recommended"] || "";

  const getPrice = (price: { taxed: number; untaxed: number } | undefined) => {
    return (vatInclusivity ? price?.taxed : price?.untaxed) ?? 0;
  };

  const hasSampleQuantity = SAMPLE_QUANTITY_PRDS.has(mpv.coreProductId);
  const lowestNonSampleQuantity = hasSampleQuantity
    ? compatibleQuantities[1]
    : compatibleQuantities[0];

  const minimumNonSampleQuantityUnitPrice = getPrice(
    prices[lowestNonSampleQuantity]?.unitDiscountedPrice
  );

  const bulkQuantity = compatibleQuantities[compatibleQuantities.length - 1].toLocaleString(locale);
  const bulkQuantityFormat = pluginData.contentful["bulkOrderQuantity"];
  let bulkOrderOptionText = bulkQuantity;
  if (bulkQuantityFormat) {
    const tokenEngine = new TokenEngine();
    bulkOrderOptionText = tokenEngine.replace(bulkQuantityFormat, {
      quantity: `${bulkQuantity}`,
    });
  }

  const recommendedQuantity = mpv.quantities.recommended;

  const choices: ProductOptionListModel[] = compatibleQuantities.map(quantity => {
    // Percent savings
    const currentQuantityTotalPrice = getPrice(prices[quantity]?.unitDiscountedPrice) * quantity;
    const currentQuantityTotalPriceWithoutDiscount = minimumNonSampleQuantityUnitPrice * quantity;
    const totalPriceSavings = currentQuantityTotalPriceWithoutDiscount - currentQuantityTotalPrice;
    const savingsPercent = Math.floor(
      (totalPriceSavings / currentQuantityTotalPriceWithoutDiscount) * 100
    );

    return {
      key: `${quantity}`,
      name: `${quantity}`,
      isCompatible: true,
      isOutOfStock: false,
      callouts:
        recommendedQuantity && quantity === recommendedQuantity
          ? [
              {
                key: "recommended",
                style: "announcement",
                text: recommendedLabel,
              } as ChoiceCallout,
            ]
          : undefined,
      totalDiscountedPrice: getPrice(prices[quantity]?.totalDiscountedPrice),
      totalListPrice: getPrice(prices[quantity]?.totalListPrice),
      unitDiscountedPrice: getPrice(prices[quantity]?.unitDiscountedPrice),
      unitListPrice: getPrice(prices[quantity]?.unitListPrice),
      differentialDiscountPrice:
        getPrice(prices[quantity]?.totalDiscountedPrice) -
        getPrice(prices[selectedQuantity]?.totalDiscountedPrice),
      differentialListPrice:
        getPrice(prices[quantity]?.totalListPrice) -
        getPrice(prices[selectedQuantity]?.totalListPrice),
      differentialUnitDiscountPrice:
        getPrice(prices[quantity]?.unitDiscountedPrice) -
        getPrice(prices[selectedQuantity]?.unitDiscountedPrice),
      differentialUnitListPrice:
        getPrice(prices[quantity]?.unitListPrice) -
        getPrice(prices[selectedQuantity]?.unitListPrice),
      savingsPercent,
    };
  });
  if (showBulkOrder(compatibleQuantities, bulkOrderUrl)) {
    choices.push({
      key: `${bulkOrderBigQuantity}`,
      name: `${bulkOrderOptionText}`,
      isCompatible: true,
    });
  }

  return (
    <ProductOptionsTranslations locale={locale}>
      <ProductOption
        productOption={{
          key: "Quantity",
          name: quantityLabel,
          style: "quantity-list",
          selectedKey: `${selectedQuantity}`,
          choices,
        }}
        savingsFormat={savingsFormat}
        hasSampleQuantity={hasSampleQuantity}
        skin={"wide"}
        tenant={PageConfig.tenant}
        locale={locale}
        currency={Object.values(prices || {})?.[0]?.currency ?? ""}
        priceDisplayMode={clientSideStateInitialized ? "unit" : "none"}
        fractionDigits={Object.values(prices || {})?.[0]?.fractionDigits ?? 2}
        onChanged={(event: ProductOptionChangedEvent) => {
          const newQuantity = event.choiceKey || "";
          selectQuantity(parseInt(newQuantity));

          if (newQuantity === `${bulkOrderBigQuantity}`) {
            setBulkQuantitySelected(true);
          }

          TrackingUtil.trackBuilderSelection({
            optionKey: "Quantity",
            valueKey: newQuantity,
          });
        }}
      />
    </ProductOptionsTranslations>
  );
};
